/**
 * P-007 c12/c17 — health-criticality data prioritization.
 *
 * Tags each queued offline record with a sync priority derived from its health-domain type, so that
 * on reconnect (and especially under limited bandwidth) the most safety-critical records —
 * medications and allergies — transmit first. Pure/deterministic (unit-testable).
 */
export const PRIORITY = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

// Health-domain → criticality. Medication + allergy edits are patient-safety critical.
const TYPE_PRIORITY = {
  medication: PRIORITY.CRITICAL,
  allergy: PRIORITY.CRITICAL,
  condition: PRIORITY.HIGH,
  vital: PRIORITY.HIGH,
  glucose: PRIORITY.HIGH,
  lab: PRIORITY.HIGH,
  meal: PRIORITY.MEDIUM,
  nutrition: PRIORITY.MEDIUM,
  hydration: PRIORITY.LOW,
  activity: PRIORITY.LOW,
  note: PRIORITY.LOW,
};

/** Priority for a record type (unknown → MEDIUM, a safe middle default). */
export function priorityFor(type) {
  const p = TYPE_PRIORITY[String(type || '').toLowerCase()];
  return p === undefined ? PRIORITY.MEDIUM : p;
}

/** Stable sort a queue of records by priority (CRITICAL first), ties broken by enqueue order (seq/id). */
export function sortByPriority(records) {
  return [...records].sort((a, b) => {
    const pa = a.priority ?? priorityFor(a.type);
    const pb = b.priority ?? priorityFor(b.type);
    if (pa !== pb) return pa - pb;
    return (a.seq ?? a.id ?? 0) - (b.seq ?? b.id ?? 0);
  });
}

/**
 * Bandwidth-aware selection: given the current connectivity mode, return the slice of the (sorted)
 * queue that should be transmitted now. On a constrained link we send only the safety-critical tier;
 * on a healthy link we drain everything.
 */
export function selectForBandwidth(records, mode) {
  const sorted = sortByPriority(records);
  if (mode === 'OFFLINE') return [];
  if (mode === 'DEGRADED') return sorted.filter((r) => (r.priority ?? priorityFor(r.type)) <= PRIORITY.HIGH);
  return sorted; // ONLINE → drain all
}
